<div class="flex flex-col gap-1.5">
  @if (label()) {
    <label 
      [for]="id() || name()"
      class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
      {{ label() }}
      @if (required()) {
        <span class="text-red-500 ml-1">*</span>
      }
    </label>
  }
  
  <div class="relative">
    <div class="relative">
      <input
        #dateInput
        type="text"
        [id]="id() || name()"
        [name]="name()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [readonly]="true"
        [value]="value() ? formatDisplayDate(value()) : ''"
        [class]="inputClasses"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (click)="$event.stopPropagation()" />
      
      @if (showCalendarIcon() && !disabled()) {
        <button
          type="button"
          class="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1.5 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 z-10"
          (click)="toggleCalendar(); $event.stopPropagation()"
          tabindex="-1"
          aria-label="Open calendar">
          <svg class="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      }
    </div>
    
    <!-- Hidden input for form submission -->
    <input
      type="date"
      [value]="value()"
      [name]="name()"
      class="hidden"
      tabindex="-1"
      aria-hidden="true" />
    
    <!-- Calendar Popup -->
    @if (showCalendar() && !disabled()) {
      <div 
        #calendarPopup
        [class]="isInModal() ? 'fixed z-[9999]' : 'absolute z-[9999]'"
        class="mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-80"
        [style]="getCalendarPosition()"
        (click)="$event.stopPropagation()"
        (mousedown)="$event.stopPropagation()"
        (mouseup)="$event.stopPropagation()"
        (touchstart)="$event.stopPropagation()">
        <!-- Calendar Header -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <button
              type="button"
              (click)="previousMonth()"
              class="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          
          <div class="flex items-center gap-2 flex-1 justify-center">
            <!-- Month Selector -->
            <div class="relative">
              <select
                [ngModel]="currentMonth()"
                (ngModelChange)="onMonthChange($event)"
                (click)="$event.stopPropagation()"
                class="px-3 py-1.5 pr-8 text-sm font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer appearance-none hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                @for (month of getMonthNames(); track $index) {
                  <option [value]="$index">{{ month }}</option>
                }
              </select>
              <div class="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            <!-- Year Selector -->
            <div class="relative">
              <select
                [ngModel]="currentYear()"
                (ngModelChange)="onYearChange($event)"
                (click)="$event.stopPropagation()"
                class="px-3 py-1.5 pr-8 text-sm font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer appearance-none hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[85px]">
                @for (year of getYears(); track year) {
                  <option [value]="year">{{ year }}</option>
                }
              </select>
              <div class="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          <div class="flex items-center gap-2">
            <button
              type="button"
              (click)="nextMonth()"
              class="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Day Names -->
        <div class="grid grid-cols-7 gap-1 mb-2">
          @for (day of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; track day) {
            <div class="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
              {{ day }}
            </div>
          }
        </div>
        
        <!-- Calendar Days -->
        <div class="grid grid-cols-7 gap-1">
          @for (day of getCalendarDays(); track $index) {
            @if (day === null) {
              <div class="aspect-square"></div>
            } @else {
              <button
                type="button"
                [disabled]="isDisabled(day)"
                (click)="selectDate(day)"
                [class]="getDayClasses(day)">
                {{ day }}
              </button>
            }
          }
        </div>
        
        <!-- Footer Buttons -->
        <div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            (click)="selectToday()"
            class="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors">
            Today
          </button>
          <button
            type="button"
            (click)="clearDate()"
            class="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
            Clear
          </button>
        </div>
      </div>
    }
  </div>
  
  @if (helpText() && !hasError) {
    <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
      {{ helpText() }}
    </span>
  }
  
  @if (hasError) {
    <span class="text-xs text-red-500 dark:text-red-400 mt-0.5 font-medium flex items-center gap-1">
      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      {{ displayError }}
    </span>
  }
</div>
